import { useEffect, useState } from 'react'
import {
  alturaQuedaEquivalenteM,
  compararEnergia,
  energiaCineticaJ,
} from '../../dominio/didatico/fisicaImpacto'
import { VELOCIDADES_IMPACTO } from '../../dominio/didatico/timelineCinematica'
import { IconeQuedaLivre } from '../icones'
import './CardFormulaCinematica.css'

const numero = (valor: number, casas = 1) =>
  valor.toLocaleString('pt-BR', { maximumFractionDigits: casas })

/** kJ com uma casa até 1 MJ; acima disso, em MJ. */
const energiaLegivel = (j: number) =>
  j >= 1e6 ? `${numero(j / 1e6, 2)} MJ` : `${numero(j / 1000, 1)} kJ`

const multiplicadorLegivel = (vezes: number) => numero(vezes, vezes < 1 ? 2 : vezes < 10 ? 1 : 0)

export interface CardFormulaCinematicaProps {
  /** Velocidade do evento na cena, em km/h. */
  velocidadeKmh: number
  /** Massa do veículo da cena: ponto de partida do controle de massa. */
  massaKg?: number
  /** Leva a velocidade escolhida à cena; chamado ao soltar o controle. */
  aoEscolherVelocidade?: (kmh: number) => void
}

/** Atalhos de massa: o que muda quando o mesmo choque envolve outro veículo. */
const ATALHOS_MASSA = [
  { rotulo: 'Moto', massaKg: 200 },
  { rotulo: 'Compacto', massaKg: 1000 },
  { rotulo: 'Sedã', massaKg: 1400 },
  { rotulo: 'SUV', massaKg: 2200 },
  { rotulo: 'Ônibus', massaKg: 13000 },
] as const

const MASSA_MIN_KG = 100
const MASSA_MAX_KG = 20000
const PASSOS_MASSA = 1000
const VELOCIDADE_MAX_KMH = 120

/** Slider logarítmico: o mesmo arrasto distingue moto de carro e carro de ônibus. */
const massaDoPasso = (passo: number) => MASSA_MIN_KG * (MASSA_MAX_KG / MASSA_MIN_KG) ** (passo / PASSOS_MASSA)
const passoDaMassa = (massaKg: number) =>
  Math.round((Math.log(massaKg / MASSA_MIN_KG) / Math.log(MASSA_MAX_KG / MASSA_MIN_KG)) * PASSOS_MASSA)
const arredondarMassa = (massaKg: number) => (massaKg < 2000 ? Math.round(massaKg / 10) * 10 : Math.round(massaKg / 100) * 100)

interface ClassificacaoTrauma {
  nivel: 'repouso' | 'leve' | 'moderado' | 'grave' | 'critico' | 'catastrofico'
  rotuloNivel: string
  corNivel: string
  mecanismo: string
  diretrizResgate: string
}

function classificarImpactoVelocidade(velocidadeKmh: number): ClassificacaoTrauma {
  if (velocidadeKmh <= 0) {
    return {
      nivel: 'repouso',
      rotuloNivel: 'Em Repouso',
      corNivel: '#64748b',
      mecanismo: 'Sem velocidade inicial. Não há dissipação de energia cinética.',
      diretrizResgate: 'Cenário estático.',
    }
  }
  if (velocidadeKmh <= 30) {
    return {
      nivel: 'leve',
      rotuloNivel: 'Baixa Energia',
      corNivel: '#22c55e',
      mecanismo:
        'Deformação contida nas zonas plásticas externas (para-choque e travessa frontal). Com cinto, risco visceral baixo.',
      diretrizResgate:
        'Avaliar chicote cervical e dor miofascial. Baixa probabilidade de invasão de habitáculo.',
    }
  }
  if (velocidadeKmh <= 50) {
    return {
      nivel: 'moderado',
      rotuloNivel: 'Energia Moderada (Urbana)',
      corNivel: '#eab308',
      mecanismo:
        'Deformação do cofre e colapso de longarinas. Ativação de pré-tensionadores e airbags. Cinto aplica forte retenção torácica.',
      diretrizResgate:
        'Investigar Síndrome do Cinto (escoriações, fraturas costais em idosos). Risco de encarceramento físico tipo I.',
    }
  }
  if (velocidadeKmh <= 70) {
    return {
      nivel: 'grave',
      rotuloNivel: 'Alta Energia Cinética',
      corNivel: '#f97316',
      mecanismo:
        'Limite de absorção da dianteira com início de intrusão do painel. Desaceleração súbita com estresse por cisalhamento de órgãos.',
      diretrizResgate:
        'Suspeita alta de trauma torácico/abdominal fechado (baço, fígado, contusão pulmonar). Desencarceramento tático necessário.',
    }
  }
  if (velocidadeKmh <= 90) {
    return {
      nivel: 'critico',
      rotuloNivel: 'Energia Crítica (Rodoviária)',
      corNivel: '#ef4444',
      mecanismo:
        'Intrusão maciça de painel e pedais. Desaceleração extrema (>30g no pico) com desaceleração violenta de vísceras contra o esqueleto.',
      diretrizResgate:
        'Risco iminente de rotura traumática de aorta e TCE grave (terceira colisão). Encarceramento físico tipo II provável.',
    }
  }
  return {
    nivel: 'catastrofico',
    rotuloNivel: 'Energia Catastrófica',
    corNivel: '#dc2626',
    mecanismo:
      'Colapso severo da célula de sobrevivência. A força cinética sobrepuja as deformações programadas do veículo.',
    diretrizResgate:
      'Lesões multissistêmicas graves com alta letalidade. Prioridade absoluta de extração rápida / protocolo crítico de trauma.',
  }
}

function descreverQueda(quedaM: number): { textoAndar: string; comparativo: string } {
  const andares = quedaM / 3
  if (andares < 0.8) {
    return {
      textoAndar: 'muro baixo / laje',
      comparativo: `queda de ${numero(quedaM, 1)} m de altura`,
    }
  }
  const andarInt = Math.max(1, Math.round(andares))
  return {
    textoAndar: `${andarInt}º andar`,
    comparativo: `queda livre do ${andarInt}º andar (${numero(quedaM, 1)} m)`,
  }
}

/**
 * Energia cinética do PHTLS (Ec = ½ · m · v²) com massa e velocidade nas mãos
 * do aluno. A massa só muda a conta; a velocidade é a do evento e vai para a
 * cena quando o controle é solto. Uma referência fixada mostra quantas vezes a
 * energia cresceu e quanto disso veio da massa (linear) e da velocidade (ao
 * quadrado).
 */
export function CardFormulaCinematica({
  velocidadeKmh,
  massaKg: massaCenaKg = 1200,
  aoEscolherVelocidade,
}: CardFormulaCinematicaProps) {
  const [massaKg, setMassaKg] = useState(massaCenaKg)
  const [velocidade, setVelocidade] = useState(velocidadeKmh)
  const [referencia, setReferencia] = useState({ massaKg: massaCenaKg, velocidadeKmh })

  // A cena pode trocar a velocidade por fora (outro evento, outro cenário).
  useEffect(() => setVelocidade(velocidadeKmh), [velocidadeKmh])

  const confirmarVelocidade = () => {
    if (velocidade !== velocidadeKmh) aoEscolherVelocidade?.(velocidade)
  }

  const vMs = velocidade / 3.6
  const energiaJ = energiaCineticaJ(massaKg, velocidade)
  const energiaRefJ = energiaCineticaJ(referencia.massaKg, referencia.velocidadeKmh)
  const comparacao = compararEnergia({ massaKg, velocidadeKmh: velocidade }, referencia)
  const escala = Math.max(energiaJ, energiaRefJ) || 1
  const quedaM = alturaQuedaEquivalenteM(velocidade)
  const infoQueda = descreverQueda(quedaM)
  const infoTrauma = classificarImpactoVelocidade(velocidade)
  const ehReferencia = referencia.massaKg === massaKg && referencia.velocidadeKmh === velocidade

  return (
    <section className="card-energia" aria-label="Energia cinética do impacto">
      <header className="card-energia__topo">
        <span className="card-energia__tag">Energia cinética · PHTLS</span>
        <span className="card-energia__nivel" style={{ color: infoTrauma.corNivel, borderColor: infoTrauma.corNivel }}>
          {infoTrauma.rotuloNivel}
        </span>
      </header>

      <div className="card-energia__formula" aria-live="polite">
        <p className="card-energia__simbolos">
          E<sub>c</sub> = ½ × <span className="card-energia__m">m</span> × <span className="card-energia__v">v²</span>
        </p>
        <p className="card-energia__numeros">
          ½ × <span className="card-energia__m">{numero(massaKg, 0)} kg</span> ×{' '}
          <span className="card-energia__v">({numero(vMs, 1)} m/s)²</span>
        </p>
        <p className="card-energia__resultado" data-testid="energia-atual">{energiaLegivel(energiaJ)}</p>
      </div>

      <div className="card-energia__controle card-energia__controle--massa">
        <div className="card-energia__rotulo">
          <span>Massa</span>
          <strong className="card-energia__m">{numero(massaKg, 0)} kg</strong>
        </div>
        <div className="card-energia__atalhos">
          {[{ rotulo: 'Carro da cena', massaKg: massaCenaKg }, ...ATALHOS_MASSA].map((atalho) => (
            <button
              key={atalho.rotulo}
              type="button"
              className={massaKg === atalho.massaKg ? 'ativo' : ''}
              aria-pressed={massaKg === atalho.massaKg}
              onClick={() => setMassaKg(atalho.massaKg)}
            >
              {atalho.rotulo}
            </button>
          ))}
        </div>
        <input
          type="range"
          aria-label="Massa do veículo"
          aria-valuetext={`${numero(massaKg, 0)} kg`}
          min={0}
          max={PASSOS_MASSA}
          value={passoDaMassa(massaKg)}
          onChange={(e) => setMassaKg(arredondarMassa(massaDoPasso(Number(e.currentTarget.value))))}
        />
      </div>

      <div className="card-energia__controle card-energia__controle--velocidade">
        <div className="card-energia__rotulo">
          <span>Velocidade</span>
          <strong className="card-energia__v">
            {numero(velocidade, 0)} km/h <small>· {numero(vMs, 1)} m/s</small>
          </strong>
        </div>
        <input
          type="range"
          aria-label="Velocidade do evento"
          aria-valuetext={`${numero(velocidade, 0)} km/h`}
          min={0}
          max={VELOCIDADE_MAX_KMH}
          step={5}
          list="marcas-velocidade-evento"
          value={velocidade}
          onChange={(e) => setVelocidade(Number(e.currentTarget.value))}
          onPointerUp={confirmarVelocidade}
          onKeyUp={confirmarVelocidade}
          onBlur={confirmarVelocidade}
        />
        <datalist id="marcas-velocidade-evento">
          {VELOCIDADES_IMPACTO.map((kmh) => <option key={kmh} value={kmh} />)}
        </datalist>
        <div className="card-energia__marcas" aria-hidden="true">
          {[0, 30, 60, 90, 120].map((kmh) => <span key={kmh}>{kmh}</span>)}
        </div>
      </div>

      <div className="card-energia__comparacao">
        <div className="card-energia__vezes">
          <strong data-testid="multiplicador">
            {comparacao.vezes === null ? '—' : `×${multiplicadorLegivel(comparacao.vezes)}`}
          </strong>
          <span>
            {ehReferencia
              ? 'esta é a referência'
              : comparacao.vezes === null
                ? 'fixe uma referência em movimento'
                : 'a energia da referência'}
          </span>
        </div>
        {comparacao.fatorMassa !== null && comparacao.fatorVelocidade !== null && (
          <p className="card-energia__fatores">
            massa <span className="card-energia__m">×{multiplicadorLegivel(comparacao.fatorMassa)}</span>
            {' · '}
            velocidade² <span className="card-energia__v">×{multiplicadorLegivel(comparacao.fatorVelocidade)}</span>
          </p>
        )}
        <div className="card-energia__barras">
          <div className="card-energia__barra card-energia__barra--ref">
            <span>Referência · {numero(referencia.massaKg, 0)} kg a {numero(referencia.velocidadeKmh, 0)} km/h</span>
            <div><i style={{ width: `${(energiaRefJ / escala) * 100}%` }} /></div>
            <em>{energiaLegivel(energiaRefJ)}</em>
          </div>
          <div className="card-energia__barra card-energia__barra--atual">
            <span>Agora · {numero(massaKg, 0)} kg a {numero(velocidade, 0)} km/h</span>
            <div><i style={{ width: `${(energiaJ / escala) * 100}%` }} /></div>
            <em>{energiaLegivel(energiaJ)}</em>
          </div>
        </div>
        <button
          type="button"
          className="card-energia__fixar"
          disabled={ehReferencia}
          onClick={() => setReferencia({ massaKg, velocidadeKmh: velocidade })}
        >
          Fixar como referência
        </button>
      </div>

      {velocidade > 0 && (
        <div className="card-energia__queda">
          <span className="card-energia__icone"><IconeQuedaLivre /></span>
          <p>
            A {numero(velocidade, 0)} km/h, bater num obstáculo rígido equivale a uma{' '}
            <strong>{infoQueda.comparativo}</strong>. <small>h = v² ÷ 2g</small>
          </p>
        </div>
      )}

      <div className="card-energia__trauma" style={{ borderLeftColor: infoTrauma.corNivel }}>
        <p><strong>Biomecânica:</strong> {infoTrauma.mecanismo}</p>
        <p><strong>Conduta de resgate:</strong> {infoTrauma.diretrizResgate}</p>
      </div>
    </section>
  )
}
